
    import { mapState, mapActions, mapMutations } from 'vuex';

    import Cookie from 'js-cookie';

    import EmailDebounceMixin from '~/components/AuthCommon/EmailDebounceMixin.vue';
    import datalayer from '@/utils/datalayer';

    import AuthEmail from '@/components/AuthApollo/AuthEmail';
    import AuthLogin from '@/components/AuthApollo/AuthLogin';
    import AuthRegister from '@/components/AuthApollo/AuthRegister';
    import AuthResetPassword from '@/components/AuthApollo/AuthResetPassword';
    import AuthEmailDebounce from '@/components/AuthCommon/AuthEmailDebounce';

    export default {
        name: 'Auth',

        components: {
            AuthEmail,
            AuthLogin,
            AuthRegister,
            AuthResetPassword,
            AuthEmailDebounce,
        },

        mixins: [EmailDebounceMixin],

        props: {
            color: {
                type: String,
                default: 'blue',
            },

            seeker: {
                type: Object,
                default: () => { return {}; },
            },

            hideLabels: {
                type: Boolean,
                default: false,
            },

            passwordPlaceholder: {
                type: String,
                default: 'Enter your password',
            },
        },

        data() {
            return {
                state: '',
                user: {},
            };
        },

        computed: {
            ...mapState({
                $_isEmailValidating: state => state.auth.isEmailValidating,
            }),

            isLogging() {
                return this.state === 'is_logging' && this.user.id;
            },

            isRegistering() {
                return this.state === 'is_registering' && !this.user.id;
            },

            isResettingPassword() {
                return this.state === 'is_resetting';
            },
        },

        watch: {
            user: {
                handler(user) {
                    this.$_RESET_EMAIL_DEBOUNCE();
                    if (user.email && user.id) {
                        this.changeStateToLogin();
                        this.resetDebounceValidation();
                    } else if (user.email && !user.id) {
                        this.changeStateToRegister();
                    } else {
                        this.resetState();
                    }
                    this.$emit('user', user);
                },
                deep: true,
            },
        },

        created() {
            if (this.seeker.email) {
                this.user = JSON.parse(JSON.stringify(this.seeker));
                this.seeker.id ? this.changeStateToLogin() : this.changeStateToRegister();
            }
        },

        methods: {
            ...mapActions('auth', {
                $_forgotPassword: 'forgotPassword',
                $_resetPassword: 'resetPassword',
            }),

            ...mapMutations('appInfo', {
                $_OPEN_SIDEBAR: 'OPEN_SIDEBAR',
            }),

            ...mapActions('me', {
                $_storeAllUserData: 'storeAllUserData',
                $_storeOnesignal: 'storeOnesignal',
            }),

            ...mapActions('eventTracking', {
                $_pushEvent: 'pushEvent',
            }),

            onUserChange(user) {
                this.user = JSON.parse(JSON.stringify(user));
                this.$emit('user', user);
            },

            changeStateToResetPassword() {
                this.state = 'is_resetting';
            },

            changeStateToRegister() {
                this.state = 'is_registering';
            },

            changeStateToLogin() {
                this.state = 'is_logging';
            },

            resetState() {
                this.state = '';
            },

            afterRegister(response) {
                this.$emit('response', response);
            },

            afterLogin(response) {
                const dataLayer = {
                    site_position: 'apply_page',
                    event: response.event === 'register' ? 'seeker_account_creation' : 'seeker_account_log_in',
                    method: response.method,
                };

                this.$_dataLayerAfterLogin(response, dataLayer);
                this.$_storeUserAfterLogin(response);
                this.$emit('afterLogin', response);
            },

            $_dataLayerAfterLogin(response, dataLayer = {}) {
                const data = Object.assign(dataLayer, {
                    seeker_id: response.data.id,
                    seeker_hash: response.data.email_hash,
                });
                this.$_pushEvent(data);
                datalayer(data);
            },

            $_storeUserAfterLogin(response) {
                this.$_storeAllUserData(response.data);
                Cookie.set(process.env.COOKIE_TOKEN_NAME, response.access_token, process.env.COOKIE_EXPIRY_TIME);
                this.$_pushOnesignal();
            },

            $_pushOnesignal() {
                if (Cookie.get(process.env.COOKIE_ONESIGNAL)) {
                    this.$_storeOnesignal({ push_id: Cookie.get(process.env.COOKIE_ONESIGNAL) }).then(() => {
                        Cookie.remove(process.env.COOKIE_ONESIGNAL);
                    });
                }
            },

            useSuggestedEmail(suggestedEmail) {
                this.user = {
                    email: suggestedEmail,
                };
            },
        },
    };
